import { useEffect, useRef, useState } from 'react';

const MOUSE_LEAVE_DEFAULT_DELAY = 3000;
const MOUSE_ENTER_DEFAULT_DELAY = 500;

export type UseMouseOverWithDelayedExitProps = {
	mouseLeaveDelay?: number;
	mouseEnterDelay?: number;
	initiallyHovered?: boolean;
	// if this value changes, the component will reset the mouse over state
	resetKey?: string;
	// if true, the component will always be considered as mouse over
	isPinned?: boolean;
	onMouseOverChange?: (isMouseOver: boolean) => void;
};

export const useMouseOverWithDelayedExit = ({
	mouseLeaveDelay = MOUSE_LEAVE_DEFAULT_DELAY,
	mouseEnterDelay = MOUSE_ENTER_DEFAULT_DELAY,
	initiallyHovered = false,
	resetKey,
	isPinned = false,
	onMouseOverChange,
}: UseMouseOverWithDelayedExitProps = {}) => {
	const [isMouseOver, setIsMouseOver] = useState(initiallyHovered);
	const hasInteractedRef = useRef(false);
	const mouseLeaveTimerRef = useRef<number | null>(null);
	const mouseEnterTimerRef = useRef<number | null>(null);
	const onMouseOverChangeRef = useRef(onMouseOverChange);

	onMouseOverChangeRef.current = onMouseOverChange;

	const createMouseEnterTimeout = (): void => {
		mouseEnterTimerRef.current = window.setTimeout(() => {
			setIsMouseOver(true);
			onMouseOverChange?.(true);
		}, mouseEnterDelay);
	};

	const handleMouseEnter = (): void => {
		if (isPinned) {
			return;
		}
		if (!hasInteractedRef.current) {
			hasInteractedRef.current = true;
		}
		if (mouseLeaveTimerRef.current) {
			window.clearTimeout(mouseLeaveTimerRef.current);
			mouseLeaveTimerRef.current = null;
		}
		if (mouseEnterTimerRef.current) {
			window.clearTimeout(mouseEnterTimerRef.current);
		}

		createMouseEnterTimeout();
	};

	const handleMouseEnterExcluded = (): void => {
		if (mouseEnterTimerRef.current) {
			window.clearTimeout(mouseEnterTimerRef.current);
			mouseEnterTimerRef.current = null;
		}
	};

	const handleMouseLeaveExcluded = (): void => {
		handleMouseEnterExcluded();
		createMouseEnterTimeout();
	};

	const handleMouseLeave = (): void => {
		if (isPinned) {
			return;
		}
		if (!hasInteractedRef.current) {
			return;
		}
		if (mouseEnterTimerRef.current) {
			window.clearTimeout(mouseEnterTimerRef.current);
			mouseEnterTimerRef.current = null;
		}
		if (mouseLeaveTimerRef.current) {
			window.clearTimeout(mouseLeaveTimerRef.current);
		}
		mouseLeaveTimerRef.current = window.setTimeout(() => {
			setIsMouseOver(false);
			onMouseOverChange?.(false);
		}, mouseLeaveDelay);
	};

	useEffect(() => {
		if (isPinned) {
			return;
		}
		hasInteractedRef.current = false;
		setIsMouseOver(initiallyHovered);
		onMouseOverChangeRef.current?.(initiallyHovered);
		if (mouseLeaveTimerRef.current) {
			window.clearTimeout(mouseLeaveTimerRef.current);
			mouseLeaveTimerRef.current = null;
		}
	}, [resetKey, initiallyHovered, isPinned]);

	useEffect(() => {
		return () => {
			if (mouseLeaveTimerRef.current) {
				window.clearTimeout(mouseLeaveTimerRef.current);
			}
			if (mouseEnterTimerRef.current) {
				window.clearTimeout(mouseEnterTimerRef.current);
			}
		};
	}, []);

	return {
		isMouseOver: isPinned || isMouseOver,
		handleMouseEnter,
		handleMouseLeave,
		handleMouseEnterExcluded,
		handleMouseLeaveExcluded,
	};
};
